import React, { useEffect, useRef, useState } from "react";
import base from "../../styles/PantallaDeProgramasAcademicos.v2.module.css"; // reutiliza estilos de modal
import tabs from "../../styles/DocentesStyles/NuevoDocenteModal.module.css";

import { getInstitutions } from '../../api/institutions';
import { getCampus } from '../../api/campus';
import { getCycles } from '../../api/ciclosEscolares';
import { getAcademias } from '../../api/academias';
import { getSeccionesAcademicas } from '../../api/secciones_academicas';
import { COUNTRIES, HORARIOS_DISPONIBLES } from '../../data/countries';
import ExpedienteAsesorTab from './ExpedienteAsesorTab';

const defaultForm = {
  fullName: "",
  institution: "",
  employeeCode: "",
  campus: "",
  section: "",
  curp: "",
  rfc: "",
  instEmail: "",
  personalEmail: "",
  academy: "",
  phone: "",
  modality: "",
  address: "",
  joinDate: "",
  birthDate: "",
  schedule: [],  // Cambiado a array para selección múltiple
  hours: "",
  nationality: "",
  originCountry: "",
  firstCycle: "",
  lastGrade: "",
  status: "ACTIVO",
  // Tipo de asesor (categoría informativa)
  advisorType: "",
  // Género (M/F/O)
  sexo: "",
  // Ficha médica
  fmTieneSeguro: "",
  fmServicioMedico: "",
  fmNumeroAfiliacion: "",
  fmNumeroClinica: "",
  fmFechaSeguro: "",
};

export default function NuevoDocenteModal({ open, onClose, onSubmit, title = "Nuevo Asesor", readOnly = false, initialForm = null }) {
  const [form, setForm] = useState(defaultForm);
  const [errors, setErrors] = useState({});
  const dialogRef = useRef(null);
  const [activeTab, setActiveTab] = useState("general");
  const [instituciones, setInstituciones] = useState([]);
  const [campusOptions, setCampusOptions] = useState([]);
  const [ciclosEscolares, setCiclosEscolares] = useState([]);
  const [academiasOptions, setAcademiasOptions] = useState([]);
  const [seccionesOptions, setSeccionesOptions] = useState([]);
  
  // Campos obligatorios con sus labels
  const requiredFields = {
    fullName: 'Nombre Completo',
    employeeCode: 'Clave Empleado',
    institution: 'Institución',
  };
  
  // Validar formulario
  const validateForm = () => {
    const newErrors = {};
    Object.entries(requiredFields).forEach(([field, label]) => {
      if (!form[field] || form[field].toString().trim() === '') {
        newErrors[field] = `${label} es obligatorio`;
      }
    });
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  
  // Cargar catálogos al abrir el modal
  useEffect(() => {
    if (!open) return;
    getInstitutions().then(data => setInstituciones(data)).catch(() => setInstituciones([]));
    getCampus().then(data => setCampusOptions(data)).catch(() => setCampusOptions([]));
    getCycles().then(data => {
      console.log('[NuevoDocenteModal] Ciclos escolares cargados:', data);
      // Ordenar ciclos de mayor a menor (por año y número de periodo)
      const sorted = [...data].sort((a, b) => {
        // Primero por año descendente
        const anioA = a.anio || parseInt((a.name || a.nombre_ciclo || '').split('-')[0]) || 0;
        const anioB = b.anio || parseInt((b.name || b.nombre_ciclo || '').split('-')[0]) || 0;
        if (anioB !== anioA) return anioB - anioA;
        // Luego por número de periodo descendente
        const periodoA = a.numero_periodo || parseInt((a.name || a.nombre_ciclo || '').split('-')[1]) || 0;
        const periodoB = b.numero_periodo || parseInt((b.name || b.nombre_ciclo || '').split('-')[1]) || 0;
        return periodoB - periodoA;
      });
      console.log('[NuevoDocenteModal] Ciclos escolares ordenados:', sorted);
      setCiclosEscolares(sorted);
    }).catch(() => setCiclosEscolares([]));
    // Cargar academias desde el backend
    getAcademias().then(data => {
      console.log('[NuevoDocenteModal] Academias cargadas:', data);
      setAcademiasOptions(data);
    }).catch(() => setAcademiasOptions([]));
    // Cargar secciones académicas desde el backend
    getSeccionesAcademicas().then(data => {
      console.log('[NuevoDocenteModal] Secciones académicas cargadas:', data);
      setSeccionesOptions(data);
    }).catch(() => setSeccionesOptions([]));
  }, [open]);

  // Catálogo inicial de materias (mock). En integración futura vendrá de props/API.
  // Materias y expediente eliminados para evitar errores durante creación

  useEffect(() => {
    if (open) {
      // Prefill si llega initialForm
      if (initialForm) {
        console.log('[NuevoDocenteModal] Inicializando con initialForm:', initialForm);
        // Usar spread completo para asegurar que todos los campos se copian
        setForm({ ...defaultForm, ...initialForm });
      } else {
        // Si no hay initialForm, resetear a default
        setForm(defaultForm);
      }
      // Limpiar errores al abrir
      setErrors({});
      const first = dialogRef.current?.querySelector("input, select, textarea, button");
      first?.focus();
    }
  }, [open, initialForm]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") onClose?.(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((f) => ({ ...f, [name]: value }));
    // Limpiar error cuando el usuario escribe
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: undefined }));
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    console.log('[NuevoDocenteModal] Enviando form:', form);
    
    // Validar antes de enviar
    if (!validateForm()) {
      console.log('[NuevoDocenteModal] Validación fallida:', errors);
      return;
    }
    
    // Enviar únicamente los campos de Información General
    onSubmit?.({ ...form });
    onClose?.();
    setForm(defaultForm);
    setErrors({});
    setActiveTab("general");
  };

  // Gestión de documentos deshabilitada temporalmente

  return (
    <div className={base.modalBackdrop} role="presentation" onMouseDown={(e) => {
      if (e.target === e.currentTarget) onClose?.();
    }}>
      <div className={base.modalContainer} role="dialog" aria-modal="true" aria-labelledby="docente-title" ref={dialogRef}>
        <div className={base.modalHeader}>
          <h2 id="docente-title" className={base.modalTitle}>{title}</h2>
          <button className={base.modalClose} aria-label="Cerrar" onClick={onClose}>✕</button>
        </div>

        <form className={base.modalBody} onSubmit={handleSubmit}>
          <fieldset disabled={readOnly} style={{ border: 0, padding: 0, margin: 0 }}>
          {/* Pestañas: Información General, Ficha Médica y Expediente */}
          <div className={tabs.tabsRow}>
            <button type="button" className={`${tabs.tab} ${activeTab === 'general' ? tabs.tabActive : ''}`} onClick={() => setActiveTab('general')}>Información General</button>
            <button type="button" className={`${tabs.tab} ${activeTab === 'fichaMedica' ? tabs.tabActive : ''}`} onClick={() => setActiveTab('fichaMedica')}>Ficha Médica</button>
            {initialForm?.id_asesor && (
              <button type="button" className={`${tabs.tab} ${activeTab === 'expediente' ? tabs.tabActive : ''}`} onClick={() => setActiveTab('expediente')}>Expediente</button>
            )}
          </div>

          {activeTab === "general" && (
            <div className={base.modalGrid}>
              <div className={base.formGroup}>
                <label className={base.label}>Nombre Completo del Asesor <span style={{color:'red'}}>*</span></label>
                <input className={`${base.input} ${errors.fullName ? base.inputError : ''}`} name="fullName" value={form.fullName} onChange={handleChange} required />
                {errors.fullName && <span style={{color:'red', fontSize:'12px'}}>{errors.fullName}</span>}
              </div>
              <div className={base.formGroup}>
                <label className={base.label}>Clave Empleado <span style={{color:'red'}}>*</span></label>
                <input className={`${base.input} ${errors.employeeCode ? base.inputError : ''}`} name="employeeCode" value={form.employeeCode} onChange={handleChange} required />
                {errors.employeeCode && <span style={{color:'red', fontSize:'12px'}}>{errors.employeeCode}</span>}
              </div>

              <div className={base.formGroup}>
                <label className={base.label}>Institución <span style={{color:'red'}}>*</span></label>
                <select className={`${base.select} ${errors.institution ? base.inputError : ''}`} name="institution" value={form.institution} onChange={handleChange} required>
                  <option value="">— Selecciona una institución —</option>
                  {Array.isArray(instituciones) && instituciones.length > 0 ? (
                    instituciones.map(inst => (
                      <option key={inst.id} value={String(inst.id)}>{inst.name}</option>
                    ))
                  ) : (
                    <option value="" disabled>Cargando instituciones...</option>
                  )}
                </select>
                {errors.institution && <span style={{color:'red', fontSize:'12px'}}>{errors.institution}</span>}
              </div>
              <div className={base.formGroup}>
                <label className={base.label}>CURP</label>
                <input className={base.input} name="curp" value={form.curp} onChange={handleChange} maxLength={18} placeholder="Máximo 18 caracteres" />
              </div>

              <div className={base.formGroup}>
                <label className={base.label}>Campus</label>
                <select className={base.select} name="campus" value={form.campus} onChange={handleChange}>
                  <option value="">— Asignar campus —</option>
                  {Array.isArray(campusOptions) && campusOptions.length > 0 ? (
                    campusOptions.map(c => (
                      <option key={c.id} value={String(c.id)}>{c.nombre}</option>
                    ))
                  ) : (
                    <option value="" disabled>Cargando campus...</option>
                  )}
                </select>
              </div>
              <div className={base.formGroup}>
                <label className={base.label}>RFC</label>
                <input className={base.input} name="rfc" value={form.rfc} onChange={handleChange} maxLength={13} placeholder="Máximo 13 caracteres" />
              </div>

              <div className={base.formGroup}>
                <label className={base.label}>Secciones</label>
                <select className={base.select} name="section" value={form.section} onChange={handleChange}>
                  <option value="">— Selecciona una sección —</option>
                  {Array.isArray(seccionesOptions) && seccionesOptions.length > 0 ? (
                    seccionesOptions.map(s => (
                      <option key={s.id} value={String(s.id)}>{s.nombre}</option>
                    ))
                  ) : (
                    <option value="" disabled>Cargando secciones...</option>
                  )}
                </select>
              </div>
              <div className={base.formGroup}>
                <label className={base.label}>Email Institucional</label>
                <input type="email" className={base.input} name="instEmail" value={form.instEmail} onChange={handleChange} />
              </div>

              <div className={base.formGroup}>
                <label className={base.label}>Academia</label>
                <select className={base.select} name="academy" value={form.academy} onChange={handleChange}>
                  <option value="">— Selecciona una academia —</option>
                  {Array.isArray(academiasOptions) && academiasOptions.length > 0 ? (
                    academiasOptions.map(a => (
                      <option key={a.id} value={String(a.id)}>{a.nombre}</option>
                    ))
                  ) : (
                    <option value="" disabled>Cargando academias...</option>
                  )}
                </select>
              </div>
              <div className={base.formGroup}>
                <label className={base.label}>Email Personal</label>
                <input type="email" className={base.input} name="personalEmail" value={form.personalEmail} onChange={handleChange} />
              </div>

              <div className={base.formGroup}>
                <label className={base.label}>Modalidad</label>
                <select className={base.select} name="modality" value={form.modality} onChange={handleChange}>
                  <option value="">— Selecciona una modalidad —</option>
                  <option value="Presencial">Presencial</option>
                  <option value="En línea">En línea</option>
                  <option value="Mixta">Mixta</option>
                </select>
              </div>
              <div className={base.formGroup}>
                <label className={base.label}>Tipo de Asesor</label>
                <select className={base.select} name="advisorType" value={form.advisorType || ''} onChange={handleChange}>
                  <option value="">— Selecciona un tipo —</option>
                  <option value="ASESOR">Asesor</option>
                  <option value="ASESOR_ADMINISTRATIVO">Asesor - Administrativo</option>
                  <option value="ADMINISTRATIVO">Administrativo</option>
                </select>
              </div>
              <div className={base.formGroup}>
                <label className={base.label}>Teléfono</label>
                <input className={base.input} name="phone" value={form.phone} onChange={handleChange} />
              </div>

              <div className={base.formGroup}>
                <label className={base.label}>Fecha de ingreso</label>
                <input type="date" className={base.input} name="joinDate" value={form.joinDate} onChange={handleChange} />
              </div>
              <div className={base.formGroup}>
                <label className={base.label}>Dirección</label>
                <input className={base.input} name="address" value={form.address} onChange={handleChange} />
              </div>

              <div className={base.formGroup}>
                <label className={base.label}>Horario Disponible</label>
                <div className={base.checkboxGroup} style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', padding: '8px 0' }}>
                  {HORARIOS_DISPONIBLES.map(horario => (
                    <label key={horario.value} style={{ display: 'flex', alignItems: 'center', gap: '4px', cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        name="schedule"
                        value={horario.value}
                        checked={Array.isArray(form.schedule) ? form.schedule.includes(horario.value) : false}
                        onChange={(e) => {
                          const currentSchedule = Array.isArray(form.schedule) ? form.schedule : [];
                          if (e.target.checked) {
                            setForm(f => ({ ...f, schedule: [...currentSchedule, horario.value] }));
                          } else {
                            setForm(f => ({ ...f, schedule: currentSchedule.filter(h => h !== horario.value) }));
                          }
                        }}
                      />
                      <span>{horario.label}</span>
                    </label>
                  ))}
                </div>
              </div>
              <div className={base.formGroup}>
                <label className={base.label}>Fecha de nacimiento</label>
                <input type="date" className={base.input} name="birthDate" value={form.birthDate} onChange={handleChange} />
              </div>

              <div className={base.formGroup}>
                <label className={base.label}>Género</label>
                <select className={base.select} name="sexo" value={form.sexo || ''} onChange={handleChange}>
                  <option value="">— Selecciona —</option>
                  <option value="M">Masculino</option>
                  <option value="F">Femenino</option>
                  <option value="O">Otro</option>
                </select>
              </div>

              <div className={base.formGroup}>
                <label className={base.label}>Horas Disponibles</label>
                <input className={base.input} name="hours" value={form.hours} onChange={handleChange} />
              </div>
              <div className={base.formGroup}>
                <label className={base.label}>Nacionalidad</label>
                <input className={base.input} name="nationality" value={form.nationality} onChange={handleChange} />
              </div>

              <div className={base.formGroup}>
                <label className={base.label}>Primer Ciclo Escolar</label>
                <select className={base.select} name="firstCycle" value={form.firstCycle} onChange={handleChange}>
                  <option value="">— Selecciona un ciclo escolar —</option>
                  {Array.isArray(ciclosEscolares) && ciclosEscolares.length > 0 ? (
                    ciclosEscolares.map(ciclo => (
                      <option key={ciclo.id} value={String(ciclo.id)}>{ciclo.name || ciclo.nombre_ciclo}</option>
                    ))
                  ) : (
                    <option value="" disabled>Cargando ciclos escolares...</option>
                  )}
                </select>
              </div>
              <div className={base.formGroup}>
                <label className={base.label}>Lugar de nacimiento</label>
                <select className={base.select} name="originCountry" value={form.originCountry} onChange={handleChange}>
                  <option value="">— Selecciona país —</option>
                  {COUNTRIES.map(country => (
                    <option key={country.code} value={country.name}>{country.name}</option>
                  ))}
                </select>
              </div>

              <div className={base.formGroup}>
                <label className={base.label}>Último Grado académico</label>
                <input className={base.input} name="lastGrade" value={form.lastGrade} onChange={handleChange} />
              </div>
              <div className={base.formGroup}>
                <label className={base.label}>Estado</label>
                <div className={base.statusSelectWrapper}>
                  <span className={`${base.statusDot} ${form.status === 'ACTIVO' ? base.dotGreen : base.dotRed}`} />
                  <select className={`${base.statusSelect} ${form.status === 'ACTIVO' ? base.activeSelect : base.inactiveSelect}`} name="status" value={form.status} onChange={handleChange}>
                    <option value="ACTIVO">ACTIVO</option>
                    <option value="INACTIVO">INACTIVO</option>
                  </select>
                </div>
              </div>
            </div>
          )}

          {/* Sección de Ficha Médica */}
          {activeTab === 'fichaMedica' && (
            <div className={base.modalGrid}>
              <div className={base.formGroup}>
                <label className={base.label}>¿Cuenta con seguro contra accidentes?</label>
                <select
                  className={base.select}
                  name="fmTieneSeguro"
                  value={form.fmTieneSeguro || ''}
                  onChange={(e) => {
                    const val = e.target.value;
                    setForm((f) => ({
                      ...f,
                      fmTieneSeguro: val,
                      // Si pasa a NO, limpiar campos dependientes
                      fmServicioMedico: val === 'SI' ? f.fmServicioMedico : '',
                      fmNumeroAfiliacion: val === 'SI' ? f.fmNumeroAfiliacion : '',
                      fmNumeroClinica: val === 'SI' ? f.fmNumeroClinica : '',
                      fmFechaSeguro: val === 'SI' ? f.fmFechaSeguro : '',
                    }));
                  }}
                >
                  <option value="">— Selecciona —</option>
                  <option value="SI">Sí</option>
                  <option value="NO">No</option>
                </select>
              </div>
              <div className={base.formGroup}>
                <label className={base.label}>Servicio médico (institución)</label>
                <input
                  className={base.input}
                  name="fmServicioMedico"
                  value={form.fmServicioMedico || ''}
                  onChange={handleChange}
                  disabled={form.fmTieneSeguro !== 'SI'}
                  placeholder="Ej. IMSS, ISSSTE, Particular..."
                  maxLength={150}
                />
              </div>

              <div className={base.formGroup}>
                <label className={base.label}>Número de afiliación</label>
                <input
                  className={base.input}
                  name="fmNumeroAfiliacion"
                  value={form.fmNumeroAfiliacion || ''}
                  onChange={handleChange}
                  disabled={form.fmTieneSeguro !== 'SI'}
                  maxLength={50}
                />
              </div>
              <div className={base.formGroup}>
                <label className={base.label}>Número de clínica</label>
                <input
                  className={base.input}
                  name="fmNumeroClinica"
                  value={form.fmNumeroClinica || ''}
                  onChange={handleChange}
                  disabled={form.fmTieneSeguro !== 'SI'}
                  maxLength={50}
                />
              </div>

              <div className={base.formGroup}>
                <label className={base.label}>Fecha de seguro</label>
                <input
                  type="date"
                  className={base.input}
                  name="fmFechaSeguro"
                  value={form.fmFechaSeguro || ''}
                  onChange={handleChange}
                  disabled={form.fmTieneSeguro !== 'SI'}
                />
              </div>
            </div>
          )}

          {/* Sección de Expediente Documental */}
          {activeTab === 'expediente' && initialForm?.id_asesor && (
            <ExpedienteAsesorTab asesor={{ id_asesor: initialForm.id_asesor }} />
          )}

          </fieldset>
          <div className={base.modalActions}>
            {!readOnly && (<button type="submit" className={base.primaryButton}>GUARDAR</button>)}
            <button type="button" className={base.secondaryButton} onClick={onClose}>{readOnly ? 'CERRAR' : 'CANCELAR'}</button>
          </div>
        </form>
      </div>
    </div>
  );
}
